import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from './components/Layout';
import { PageLoader } from './components/LoadingSpinner';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AssistantProvider } from './contexts/AssistantContext';
import useIsMobile from './hooks/useIsMobile';
import './styles/animations.css';

// Pages d'entrée - imports directs (chargées immédiatement)
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CGUPage from './pages/CGUPage';

// Pages protégées - lazy loading (chargées à la demande)
const ChatbotPage = lazy(() => import('./pages/ChatbotPage'));
const ChatbotAssistant = lazy(() => import('./pages/ChatbotAssistant'));
const BiblioRag = lazy(() => import('./pages/BiblioRag'));
const WebSearch = lazy(() => import('./pages/WebSearch'));
const ChatbotPageOpenAI = lazy(() => import('./pages/ChatbotPageOpenAI'));
const DocumentSearchPage = lazy(() => import('./pages/DocumentSearchPage'));
const PromptManagementPage = lazy(() => import('./pages/PromptManagementPage'));
const AidePage = lazy(() => import('./pages/AidePage'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const AssistantCatalogue = lazy(() => import('./pages/AssistantCatalogue'));
const AssistantManagementPage = lazy(() => import('./pages/AssistantManagementPage'));
const MyAssistantsPage = lazy(() => import('./pages/MyAssistantsPage'));
const MySpacePage = lazy(() => import('./pages/MySpacePage'));
const Avatars = lazy(() => import('./pages/Avatars'));
const HomePage = lazy(() => import('./pages/HomePage'));
const AudioTranscriptionPage = lazy(() => import('./pages/AudioTranscriptionPage'));
const AdminStatsPage = lazy(() => import('./pages/AdminStatsPage'));
const EmailWriterPage = lazy(() => import('./pages/EmailWriterPage'));
const FeedbackManagementPage = lazy(() => import('./pages/FeedbackManagementPage'));

// UI mobile dédiée (chargée à la demande)
const MobileApp = lazy(() => import('./mobile/MobileApp'));

// Variantes d'animation pour les transitions de page
const pageVariants = {
  initial: {
    opacity: 0,
    y: 10
  },
  in: {
    opacity: 1,
    y: 0
  },
  out: {
    opacity: 0,
    y: -10
  }
};

// Paramètres de transition
const pageTransition = {
  type: 'tween',
  ease: 'easeInOut',
  duration: 0.3
};

// Composant pour réinitialiser le scroll lors des changements de page
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Composant de redirection pour la page d'accueil
const HomeRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader text="Chargement..." />;
  return isAuthenticated ? <Navigate to="/home" replace /> : <LandingPage />;
};

// Composant qui applique les transitions sélectivement
const AnimatedRoutes = () => {
  const location = useLocation();

  // Pages sans animation
  const noAnimationPages = ['/aide', '/login', '/register', '/forgot-password', '/reset-password', '/cgu'];
  const isNoAnimationPage = noAnimationPages.some(path => location.pathname.startsWith(path));

  return (
    <AnimatePresence mode="wait">
      {isNoAnimationPage ? (
        // Rendu sans animation - avec Suspense pour AidePage (lazy)
        <Suspense fallback={<PageLoader text="Chargement..." />}>
          <Routes location={location}>
            <Route path="/aide" element={<AidePage />} />
            <Route path="/cgu" element={<CGUPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </Routes>
        </Suspense>
      ) : (
        // Rendu avec animation pour les autres pages - avec Suspense
        <Suspense fallback={<PageLoader text="Chargement..." />}>
          <motion.div
            key={location.pathname + location.search}
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
            className="h-full"
          >
            <Routes location={location}>
            <Route path="/" element={<HomeRedirect />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/chatbot-assistant" 
              element={
                <ProtectedRoute>
                  <ChatbotAssistant />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/chatbot"
              element={
                <ProtectedRoute>
                  <ChatbotPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/biblio-rag"
              element={
                <ProtectedRoute>
                  <BiblioRag />
                </ProtectedRoute>
              }
            />
            <Route
              path="/websearch"
              element={
                <ProtectedRoute>
                  <WebSearch />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assistants-admin" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AssistantManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chatbotopenai" 
              element={
                <ProtectedRoute>
                  <ChatbotPageOpenAI />
                </ProtectedRoute>
              } 
            />       
            <Route 
              path="/document-search" 
              element={
                <ProtectedRoute>
                  <DocumentSearchPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/prompts" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <PromptManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/assistants"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AssistantCatalogue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mes-assistants"
              element={
                <ProtectedRoute>
                  <MyAssistantsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/avatars"
              element={
                <ProtectedRoute>
                  <Avatars />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-space"
              element={
                <ProtectedRoute>
                  <MySpacePage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <UserManagement />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transcription"
              element={
                <ProtectedRoute>
                  <AudioTranscriptionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-writer"
              element={
                <ProtectedRoute>
                  <EmailWriterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/stats"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminStatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feedbacks"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <FeedbackManagementPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          </motion.div>
        </Suspense>
      )}
    </AnimatePresence>
  );
};

// Aiguillage mobile / bureau : sur smartphone, on rend l'UI mobile dédiée.
const RootRouter = () => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Suspense fallback={<PageLoader text="Chargement..." />}>
        <MobileApp />
      </Suspense>
    );
  }

  return (
    <Layout>
      <AnimatedRoutes />
    </Layout>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AssistantProvider>
          <ScrollToTop />
          <RootRouter />
        </AssistantProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;